// app/src/lib/reportDoc.ts
// One professionally-designed, branded document used for every downloadable report.
// Renders to a self-contained HTML string that the in-app document viewer shows and
// prints (the device's native "Save as PDF" turns it into a clean PDF). Branding
// (business name, address, VAT, footer) is pulled from the same receipt config used
// on receipts and invoices, so every report carries a consistent letterhead.
import { cachedReceiptConfig, loadReceiptConfig } from "./receipt";
import { fmtDateTime } from "./datetime";

const esc = (v: unknown) =>
  String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// Right-align cells that read as a number or money value (e.g. "80", "£80.00", "-12.50").
const NUMERIC = /^-?[£$€₨]?\s?\d[\d,]*(\.\d+)?%?$/;
const isNumeric = (v: unknown) => typeof v === "number" || (typeof v === "string" && NUMERIC.test(v.trim()));

export interface ReportDocOptions {
  title: string;
  subtitle?: string;              // e.g. the period
  columns: string[];
  rows: (string | number | null)[][];
  totals?: (string | number)[];   // optional bold totals row
  note?: string;                  // optional small note under the table
  /** Headline figures printed above the table (label → value). */
  summary?: { label: string; value: string }[];
  /** Encoded into the QR in the footer. Defaults to the page the report came
   *  from, so a printed sheet can be traced back to the live screen. */
  qrData?: string;
}

/** Build a branded, print-ready report document. Returns a full HTML string. */
export async function buildReportHtml(opts: ReportDocOptions): Promise<string> {
  const cfg = cachedReceiptConfig() ?? (await loadReceiptConfig());
  const h = cfg.header ?? { name: "" };
  const contact = [h.phone, h.email].filter(Boolean).map(esc).join(" &middot; ");
  const headLines = [
    h.address ? `<div class="addr">${esc(h.address)}</div>` : "",
    contact ? `<div class="addr">${contact}</div>` : "",
    h.vat_number ? `<div class="addr">VAT No. ${esc(h.vat_number)}</div>` : "",
  ].join("");

  const align = opts.rows.length
    ? opts.columns.map((_, i) => opts.rows.some((r) => isNumeric(r[i])))
    : opts.columns.map(() => false);

  const th = opts.columns.map((c, i) => `<th class="${align[i] ? "r" : "l"}">${esc(prettify(c))}</th>`).join("");
  const body = opts.rows.length
    ? opts.rows.map((r) =>
        `<tr>${opts.columns.map((_, i) => `<td class="${align[i] ? "r" : "l"}">${esc(r[i])}</td>`).join("")}</tr>`).join("")
    : `<tr><td class="empty" colspan="${opts.columns.length}">No records for this period.</td></tr>`;
  const tot = opts.totals
    ? `<tr class="totals">${opts.columns.map((_, i) => `<td class="${align[i] ? "r" : "l"}">${esc(opts.totals![i] ?? "")}</td>`).join("")}</tr>`
    : "";

  const generated = fmtDateTime(Date.now(), { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  // The brand mark, and a QR back to the screen this came from — a printed
  // report that has been photocopied twice still says where it originated.
  const logo = `<img class="logo" src="${location.origin}/brand/logo.png" alt="">`;
  const qrTarget = opts.qrData ?? location.href;
  const qr = `<img class="qr" src="/api/public/qr?d=${encodeURIComponent(qrTarget)}&s=180" alt="">`;
  const summary = opts.summary?.length
    ? `<div class="summary">${opts.summary.map((x) =>
        `<div class="sum"><span class="k">${esc(x.label)}</span><span class="v">${esc(x.value)}</span></div>`).join("")}</div>`
    : "";

  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(opts.title)}</title>
<style>
  :root { --ink:#16181d; --muted:#6b7280; --line:#e5e7eb; --accent:#BB2D22; }
  * { box-sizing: border-box; }
  html,body { margin:0; background:#f2f3f5; }
  body { font:13px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif; color:var(--ink); }
  .sheet { max-width:880px; margin:0 auto; background:#fff; padding:40px 44px 56px; min-height:100%; }
  .head { display:flex; justify-content:space-between; align-items:flex-start; gap:24px; padding-bottom:18px; border-bottom:3px solid var(--ink); }
  .brandrow { display:flex; align-items:center; gap:12px; }
  .logo { height:44px; width:auto; }
  .summary { display:flex; flex-wrap:wrap; gap:1px; margin-top:20px; background:var(--line); border:1px solid var(--line); border-radius:6px; overflow:hidden; }
  .sum { flex:1 1 130px; background:#fff; padding:10px 12px; }
  .sum .k { display:block; font-size:9.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--muted); }
  .sum .v { display:block; font-size:17px; font-weight:800; margin-top:2px; font-variant-numeric:tabular-nums; }
  .qr { width:64px; height:64px; }
  .footqr { display:flex; align-items:center; gap:10px; }
  .biz { font-size:22px; font-weight:800; letter-spacing:-.01em; }
  .legal { color:var(--muted); font-size:12.5px; margin-top:1px; }
  .addr { color:#555; font-size:12px; margin-top:3px; }
  .doc { text-align:right; flex-shrink:0; }
  .doc .t { font-size:18px; font-weight:800; text-transform:uppercase; letter-spacing:.04em; color:var(--accent-text); }
  .doc .s { color:var(--muted); font-size:12.5px; margin-top:4px; }
  .doc .g { color:var(--muted); font-size:11px; margin-top:6px; }
  table { width:100%; border-collapse:collapse; margin-top:22px; }
  thead th { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--muted);
             padding:9px 10px; border-bottom:1.5px solid var(--ink); background:#fafafa; }
  tbody td { padding:8px 10px; border-bottom:1px solid var(--line); font-size:12.5px; vertical-align:top; }
  tbody tr:nth-child(even) td { background:#fbfbfc; }
  .l { text-align:left; } .r { text-align:right; white-space:nowrap; font-variant-numeric:tabular-nums; }
  .totals td { font-weight:800; border-top:2px solid var(--ink); border-bottom:none; background:#fff; padding-top:11px; }
  .empty { text-align:center; color:var(--muted); padding:28px 10px; }
  .note { color:var(--muted); font-size:11.5px; margin-top:14px; }
  .foot { margin-top:34px; padding-top:12px; border-top:1px solid var(--line); color:var(--muted); font-size:11px;
          display:flex; justify-content:space-between; gap:16px; }
  @media print {
    @page { size:A4; margin:14mm; @bottom-right { content:"Page " counter(page) " of " counter(pages); font-size:9px; color:#999; } }
    html,body { background:#fff; }
    .sheet { max-width:none; margin:0; padding:0; }
    thead { display:table-header-group; }
    tr { break-inside:avoid; }
  }
</style></head>
<body><div class="sheet">
  <div class="head">
    <div class="brandrow">
      ${logo}
      <div>
      <div class="biz">${esc(h.name || "Your business")}</div>
      ${h.legal_name ? `<div class="legal">${esc(h.legal_name)}</div>` : ""}
      ${headLines}
      </div>
    </div>
    <div class="doc">
      <div class="t">${esc(opts.title)}</div>
      ${opts.subtitle ? `<div class="s">${esc(opts.subtitle)}</div>` : ""}
      <div class="g">Generated ${esc(generated)}</div>
    </div>
  </div>
  ${summary}
  <table>
    <thead><tr>${th}</tr></thead>
    <tbody>${body}${tot}</tbody>
  </table>
  ${opts.note ? `<div class="note">${esc(opts.note)}</div>` : ""}
  <div class="foot">
    <span class="footqr">${qr}<span>${esc(cfg.footer || "")}</span></span>
    <span>${esc(h.name || "")}${opts.rows.length ? ` &middot; ${opts.rows.length} row${opts.rows.length === 1 ? "" : "s"}` : ""}</span>
  </div>
</div></body></html>`;
}

/** "sold_at_utc" → "Sold at utc"; "line_total" → "Line total". */
function prettify(key: string): string {
  const s = key.replace(/_/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
